import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import {
  CloudUpload,
  List as ListIcon,
  PictureAsPdf,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import axios from "axios";
import {BASE_URL , URL_BASE} from '../../../../../utils';

export const PatientDocumentsPage = () => {
  const { patientId } = useParams();

  const [tab, setTab] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
 
  // 🔹 Fetch documents
  const fetchDocuments = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/patient-documents/${patientId}`
      );
      setDocuments(res.data.data);
    } catch {
      setError("Failed to load documents");
    }
  };

  useEffect(() => {
    if (patientId) fetchDocuments();
  }, [patientId]);

  // 🔹 Upload document
  const handleUpload = async () => {
    if (!file || !documentType) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("document", file);
      formData.append("documentType", documentType);
      formData.append("patientId", patientId);

      await axios.post(
        `${BASE_URL}/patient-documents/upload`,
        formData
      );

      setFile(null);
      setDocumentType("");
      fetchDocuments();
      setTab(1);
    } catch {
      setError("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  if (!patientId) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Patient ID not provided</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: "#f4f6f8", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" fontWeight={700}>
            Patient Documents
          </Typography>
          <Chip
            sx={{ mt: 1 }}
            label={`Patient ID: ${patientId}`}
            color="primary"
            variant="outlined"
          />
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="fullWidth"
          >
            <Tab icon={<CloudUpload />} label="Upload Document" />
            <Tab icon={<ListIcon />} label="Document List" />
          </Tabs>
        </Paper>

        {/* Upload Tab */}
        {tab === 0 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Upload Patient Document
            </Typography>

            <TextField
              select
              fullWidth
              label="Document Type"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="Aadhar">Aadhar</MenuItem>
              <MenuItem value="Prescription">Prescription</MenuItem>
              <MenuItem value="Lab Report">Lab Report</MenuItem>
              <MenuItem value="Discharge Summary">
                Discharge Summary
              </MenuItem>
            </TextField>

            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
            />

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                disabled={!file || !documentType || loading}
                onClick={handleUpload}
              >
                Upload Document
              </Button>
            </Box>
          </Paper>
        )}

        {/* List Tab */}
        {tab === 1 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Uploaded Documents
            </Typography>

            {documents.length === 0 ? (
              <Alert severity="info">No documents uploaded</Alert>
            ) : (
              <List>
                {documents.map((doc) => (
                  <ListItem
                    key={doc._id}
                    secondaryAction={
                      <IconButton
                        component="a"
                        href={`${URL_BASE}/${doc.filePath}`}
                        target="_blank"
                      >
                        <PictureAsPdf />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={doc.documentType}
                      secondary={doc.fileName}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        )}
      </Container>
    </Box>
  );
};
